/**
 * NUTRITION PLANS
 *
 * Single source of truth pour le mapping goalId → plan nutritionnel.
 * Utilisé par NutritionPage, WeeklyMealPlan, DashboardHome (kcal affichées).
 *
 * Calculs basés sur un profil "standard" ; seront raffinés quand on aura
 * le poids/taille/activité dans les calculs personnalisés.
 */

export type NutritionPlanId = "seche" | "maintien" | "masse"

export interface NutritionPlan {
  id: NutritionPlanId
  label: string
  shortLabel: string
  dailyCalories: number
  macros: {
    protein: number   // grammes
    carbs: number     // grammes
    fat: number       // grammes
  }
  macrosPct: {
    protein: number   // %
    carbs: number     // %
    fat: number       // %
  }
  description: string
  color: string
}

export const NUTRITION_PLANS: Record<NutritionPlanId, NutritionPlan> = {
  seche: {
    id: "seche",
    label: "Sèche / Définition",
    shortLabel: "Sèche",
    dailyCalories: 1937,
    macros: { protein: 165, carbs: 180, fat: 55 },
    macrosPct: { protein: 34, carbs: 37, fat: 29 },
    description: "Déficit calorique contrôlé pour perdre du gras en préservant le muscle.",
    color: "#22c55e",
  },
  maintien: {
    id: "maintien",
    label: "Maintien",
    shortLabel: "Maintien",
    dailyCalories: 2421,
    macros: { protein: 150, carbs: 280, fat: 75 },
    macrosPct: { protein: 25, carbs: 46, fat: 29 },
    description: "Équilibre énergétique pour stabiliser poids et performances.",
    color: "#06b6d4",
  },
  masse: {
    id: "masse",
    label: "Prise de masse",
    shortLabel: "Masse",
    dailyCalories: 2663,
    macros: { protein: 170, carbs: 340, fat: 80 },
    macrosPct: { protein: 26, carbs: 51, fat: 23 },
    description: "Surplus calorique pour construire du muscle avec un minimum de gras.",
    color: "#f97316",
  },
}

/**
 * Mapping goal_id → plan nutritionnel.
 * Doit rester synchronisé entre NutritionPage, WeeklyMealPlan et DashboardHome.
 */
export const GOAL_TO_PLAN: Record<string, NutritionPlanId> = {
  // Sèche (déficit calorique)
  "perte-de-poids": "seche",
  "seche-definition": "seche",
  "decoupe-extreme": "seche",
  // Maintien (équilibre)
  "remise-en-forme": "maintien",
  "force-endurance": "maintien",
  "force-athletique": "maintien",
  // Masse (surplus calorique)
  "prise-de-muscle": "masse",
  "construction-musculaire": "masse",
  "hypertrophie": "masse",
}

/**
 * Retourne le plan nutritionnel pour un goalId donné.
 * Fallback sur "maintien" si goalId inconnu.
 */
export function getPlanForGoal(goalId: string | undefined | null): NutritionPlan {
  if (!goalId) return NUTRITION_PLANS.maintien
  const planId = GOAL_TO_PLAN[goalId] ?? "maintien"
  return NUTRITION_PLANS[planId]
}

// ============================================
// IDÉES DE REPAS PAR MOMENT DE LA JOURNÉE
// ============================================

export interface MealIdea {
  title: string
  description: string
  icon: string // emoji ou nom Lucide
}

export const MEAL_IDEAS_BY_PLAN: Record<NutritionPlanId, Record<string, MealIdea[]>> = {
  seche: {
    breakfast: [
      { title: "Omelette légère", description: "3 blancs + 1 œuf entier, épinards, tomates", icon: "🍳" },
      { title: "Skyr + fruits rouges", description: "200g skyr, myrtilles, flocons d'avoine", icon: "🥣" },
      { title: "Pancakes protéinés", description: "Avoine, blanc d'œuf, banane, cannelle", icon: "🥞" },
    ],
    lunch: [
      { title: "Poulet grillé + légumes", description: "150g poulet, brocolis, patate douce", icon: "🍗" },
      { title: "Bowl saumon-quinoa", description: "120g saumon, quinoa, avocat, roquette", icon: "🍱" },
      { title: "Wrap dinde-crudités", description: "Galette complète, dinde, salade, yaourt nature", icon: "🌯" },
    ],
    snack: [
      { title: "Fromage blanc + amandes", description: "150g fromage blanc 0%, 15g amandes", icon: "🥛" },
      { title: "Pomme + beurre d'amande", description: "1 pomme, 1 c. à soupe beurre d'amande", icon: "🍎" },
    ],
    dinner: [
      { title: "Cabillaud vapeur + courgettes", description: "150g cabillaud, courgettes grillées, riz basmati", icon: "🐟" },
      { title: "Soupe + œufs durs", description: "Soupe de légumes maison, 2 œufs durs", icon: "🥣" },
      { title: "Steak haché 5% + salade", description: "120g steak haché, salade verte, quinoa", icon: "🥗" },
    ],
  },
  maintien: {
    breakfast: [
      { title: "Porridge classique", description: "60g avoine, lait, banane, miel", icon: "🥣" },
      { title: "Tartines complètes", description: "2 tranches pain complet, œufs brouillés, avocat", icon: "🍞" },
      { title: "Smoothie énergie", description: "Banane, avocat, protéine, beurre d'amande, lait", icon: "🥤" },
    ],
    lunch: [
      { title: "Pâtes complètes + poulet", description: "80g pâtes, 150g poulet, sauce tomate maison", icon: "🍝" },
      { title: "Buddha bowl", description: "Quinoa, pois chiches, légumes rôtis, tahini", icon: "🥙" },
      { title: "Riz + saumon teriyaki", description: "80g riz basmati, 150g saumon, brocoli", icon: "🍣" },
    ],
    snack: [
      { title: "Banane + beurre de cacahuète", description: "1 banane, 1 c. à soupe beurre de cacahuète", icon: "🍌" },
      { title: "Yaourt grec + granola", description: "150g yaourt grec, 30g granola, fruits rouges", icon: "🥣" },
    ],
    dinner: [
      { title: "Poisson blanc + riz", description: "150g cabillaud, riz basmati, légumes vapeur", icon: "🐟" },
      { title: "Bowl tex-mex", description: "Riz, haricots rouges, maïs, avocat, poulet", icon: "🌮" },
      { title: "Soupe miso + sushis", description: "Soupe miso, 8 sushis, salade d'algues", icon: "🍣" },
    ],
  },
  masse: {
    breakfast: [
      { title: "Porridge royal", description: "100g avoine, lait entier, banane, beurre de cacahuète", icon: "🥣" },
      { title: "Tartines beurre/miel", description: "3 tranches pain complet, beurre, miel, œufs", icon: "🍞" },
      { title: "Smoothie gainer", description: "Banane, avoine, lait entier, protéine, noix", icon: "🥤" },
    ],
    lunch: [
      { title: "Pâtes bolognaise", description: "120g pâtes, 150g bœuf haché, sauce tomate", icon: "🍝" },
      { title: "Riz + poulet + légumes", description: "120g riz, 200g poulet, brocoli, huile d'olive", icon: "🍚" },
      { title: "Pizza maison complète", description: "Pâte complète, mozzarella, poulet, légumes", icon: "🍕" },
    ],
    snack: [
      { title: "Sandwich dinde-avocat", description: "Pain complet, dinde, avocat, tomate", icon: "🥪" },
      { title: "Shake post-training", description: "Lait entier, banane, protéine, flocons d'avoine", icon: "🥛" },
    ],
    dinner: [
      { title: "Saumon + patates douces", description: "180g saumon, 250g patate douce, légumes verts", icon: "🐟" },
      { title: "Steak + pâtes", description: "180g steak, 100g pâtes complètes, beurre à l'ail", icon: "🥩" },
      { title: "Poulet rôti + purée", description: "200g poulet, purée maison, haricots verts", icon: "🍗" },
    ],
  },
}

/**
 * Retourne une idée de repas selon l'heure actuelle et le plan.
 */
export function getMealIdeaForNow(planId: NutritionPlanId): { meal: string; idea: MealIdea } {
  const hour = new Date().getHours()
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000)

  let mealKey: keyof typeof MEAL_IDEAS_BY_PLAN.seche
  let mealLabel: string

  if (hour < 10) {
    mealKey = "breakfast"
    mealLabel = "Petit-déjeuner"
  } else if (hour < 14) {
    mealKey = "lunch"
    mealLabel = "Déjeuner"
  } else if (hour < 18) {
    mealKey = "snack"
    mealLabel = "Collation"
  } else {
    mealKey = "dinner"
    mealLabel = "Dîner"
  }

  const ideas = MEAL_IDEAS_BY_PLAN[planId][mealKey]
  const idea = ideas[dayOfYear % ideas.length]

  return { meal: mealLabel, idea }
}
